import type { FetchOptions } from 'ofetch'

import type {
	ApiState,
	ApiSuccessResponse,
	ExtendedFetchOptions
} from '@/types/api-client'

import type {
	AddRolePermissionPayload,
	AssignRolePayload,
	CreateRolePayload,
	RemoveRolePayload,
	RemoveRolePermissionPayload,
	SuperAdminRole,
	UpdateRolePayload
} from '@/types/api-client/super-admin-roles'

// Super Admin Roles API helper
export function createSuperAdminRolesApi(
	request: <T>(endpoint: string, options?: ExtendedFetchOptions) => Promise<T | null>,
	createApiState: <T>(endpoint: string, options?: FetchOptions) => ApiState<T>
) {
	return {
		/**
		 * Get single role (return not reactive)
		 * @param roleId route param
		 * @example
		 * ``` typescript
		 * await api.superAdminRoles.getOne(1)
		 * ```
		 * @returns Promise<SuperAdminRole>
		 */
		getOne: (roleId: number) =>
			request<SuperAdminRole>(`/sadmin/roles/${roleId}`),

		/**
		 * Create role
		 * @param payload body params
		 * @param options optional fetch config
		 * @returns Promise<SuperAdminRole>
		 * @example
		 * ``` typescript
		 * await api.superAdminRoles.create({ name: 'New Role' })
		 * ```
		 */
		create: (payload: CreateRolePayload, options: ExtendedFetchOptions = {}) => {
			return request<SuperAdminRole>('/sadmin/roles', {
				method: 'POST',
				body: payload,
				...options,
				errorsAllowed: options.errorsAllowed || {
					titles: { '': 'errors.test_title' },
					messages: { '': 'errors.test_message' }
				}
			})
		},

		/**
		 * Update role
		 * @param roleId route param
		 * @param payload body params
		 * @param options optional fetch config
		 * @returns Promise<SuperAdminRole>
		 * @example
		 * ``` typescript
		 * await api.superAdminRoles.update(1, { name: 'New Role Name' })
		 * ```
		 */
		update: (roleId: number, payload: UpdateRolePayload, options: ExtendedFetchOptions = {}) => {
			return request<SuperAdminRole>(`/sadmin/roles/${roleId}`, {
				method: 'PUT',
				body: payload,
				...options
			})
		},
		/**
		 * Delete role
		 * @param roleId route param
		 * @param options optional auto-error config
		 * @returns Promise<ApiSuccessResponse>
		 * @example
		 * ``` typescript
		 * await api.superAdminRoles.delete(1)
		 * ```
		 */
		delete: (roleId: number, options: ExtendedFetchOptions = {}) =>
			request<ApiSuccessResponse>(`/sadmin/roles/${roleId}`, {
				method: 'DELETE',
				...options
			}),

		/**
		 * Add permission(s) to role
		 * @param roleId route param
		 * @param payload body params
		 * @param options optional fetch config
		 * @returns Promise<ApiSuccessResponse>
		 * @example
		 * ``` typescript
		 * await api.superAdminRoles.addPermission(1, { permissionIds: [2] })
		 * ```
		 */
		addPermission: (roleId: number, payload: AddRolePermissionPayload, options: ExtendedFetchOptions = {}) => {
			return request<ApiSuccessResponse>(`/sadmin/roles/${roleId}/permissions`, {
				method: 'POST',
				body: payload,
				...options
			})
		},

		/**
		 * Remove permission(s) from role
		 * @param roleId route param
		 * @param payload body params
		 * @param options optional fetch config
		 * @returns Promise<ApiSuccessResponse>
		 * @example
		 * ``` typescript
		 * await api.superAdminRoles.removePermission(1, { permissionIds: [2] })
		 * ```
		 */
		removePermission: (roleId: number, payload: RemoveRolePermissionPayload, options: ExtendedFetchOptions = {}) => {
			return request<ApiSuccessResponse>(`/sadmin/roles/${roleId}/permissions`, {
				method: 'DELETE',
				body: payload,
				...options
			})
		},

		// Reactive state methods (destructurable)
		/**
		 * Get all roles (return reactive)
		 * @param options optional fetch config
		 * @returns Promise<ApiState<SuperAdminRole[]>>
		 * @example
		 * ``` typescript
		 * const { data, error, pending, refresh } = api.superAdminRoles.useGetAll()
		 * ```
		 */
		useGetAll: (options: ExtendedFetchOptions = {}): ApiState<SuperAdminRole[]> =>
			createApiState<SuperAdminRole[]>('/sadmin/roles', options),

		/**
		 * Get single role (return reactive)
		 * @param roleId route param
		 * @param options optional fetch config
		 * @return Promise<ApiState<SuperAdminRole>>
		 * @example
		 * ``` typescript
		 * const { data, error, pending, refresh } = api.superAdminRoles.useGetOne(1)
		 * ```
		 */
		useGetOne: (roleId: number, options: ExtendedFetchOptions = {}): ApiState<SuperAdminRole> =>
			createApiState<SuperAdminRole>(`/sadmin/roles/${roleId}`, options),

		/**
         * Assign permissions to user
         * @param payload body params
         * @param options optional fetch config
         * @returns Promise<ApiSuccessResponse>
         * @example
         * ``` typescript
         * await api.superAdminRoles.assignToUser({ emailId: 1, roleId: 5 })
         * ```
         */
		assignToUser: (payload: AssignRolePayload, options: ExtendedFetchOptions = {}) => {
			return request<ApiSuccessResponse>('/sadmin/users/role', {
				method: 'POST',
				body: payload,
				...options
			})
		},

		/**
         * Remove permissions from user
         * @param payload body params
         * @param options optional fetch config
         * @returns Promise<ApiSuccessResponse>
         * @example
         * ``` typescript
         * await api.superAdminRoles.removeFromUser({ emailId: 1, roleId: 5 })
         * ```
         */
		removeFromUser: (payload: RemoveRolePayload, options: ExtendedFetchOptions = {}) => {
			return request<ApiSuccessResponse>('/sadmin/users/role', {
				method: 'DELETE',
				body: payload,
				...options
			})
		}
	}
}
